import React, { useCallback, useEffect, useState } from "react";
import { Badge } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";

import Constants from "../../constants";
import HttpManager from "../../services/HttpManager";
import Utils from "../../utils/utils";
import { getErrorMessage } from "../../common";
import { AdvisorSelection, EnrollmentHistoryDTO } from "../../models/EnrollmentHistoryDTO";
import EnrollmentNotesTimeline from "../../components/enrollment/enrollmentNotesTimeline";
import AdvisorsPicker from "../../components/enrollment/advisorsPicker";
import { formatLocalDateTime } from "../../components/enrollment/format";
import { horarioBonito } from "../../utils/horarioBonito";
import EnrollStudentModal from "./enrollStudentModal";
import { useDispatch } from "react-redux";
import ModalComponent from "../../components/modals/modal/modal";
import UpdateStudentEnrollmentComponent from "../management/enrollments/updateStudentEnrollment";
import { setCourse, setTuitions } from "../../redux/reducers/scheduleReducer";
import { ENROLLMENT_STATUS } from "../../constants";

export interface StudentHistoryTabProps {
	studentId: number;
	studentName: string;
	studentIdentityCode?: string;
	campusId: number;
	canEnroll: boolean;
	/** Called after a successful enrollment so the parent list can refresh. */
	onEnrolled?: () => void;
}

/**
 * "Historial" tab of the student profile: every enrollment (any campus) with who enrolled them and when,
 * the advisors credited, the note history and an attendance summary. Also hosts the "Matricular" button.
 */
const StudentHistoryTab: React.FC<StudentHistoryTabProps> = ({ studentId, studentName, studentIdentityCode, campusId, canEnroll, onEnrolled }) => {
	const http = HttpManager.getInstance();
	const dispatch = useDispatch<any>();
	const [updatingEnrollmentId, setUpdatingEnrollmentId] = useState<number | null>(null);
	const closeUpdate = () => {
		dispatch(setCourse(null));
		dispatch(setTuitions([]));
		setUpdatingEnrollmentId(null);
	};
	const [history, setHistory] = useState<EnrollmentHistoryDTO[] | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [showEnroll, setShowEnroll] = useState<boolean>(false);
	const [editingAdvisorsOf, setEditingAdvisorsOf] = useState<number | null>(null);
	//Acepta null porque el selector distingue "sin responder" de "Ninguno"; al guardar una
	//matrícula ya existente, sin responder equivale a dejarla sin asesoras.
	const [advisorsDraft, setAdvisorsDraft] = useState<AdvisorSelection[] | null>([]);
	const [savingAdvisors, setSavingAdvisors] = useState<boolean>(false);
	const [advisorsError, setAdvisorsError] = useState<string | null>(null);

	const load = useCallback(() => {
		setError(null);
		http.enrollmentService.getStudentHistory(campusId, studentId)
			.then((response) => setHistory(response.data))
			.catch((e) => { setHistory([]); setError(getErrorMessage(e)); });
	}, [campusId, studentId]);

	useEffect(() => { load(); }, [load]);

	const startEditingAdvisors = (enrollment: EnrollmentHistoryDTO) => {
		setAdvisorsError(null);
		setAdvisorsDraft(enrollment.advisors.map((advisor) => ({ userId: advisor.userId, name: advisor.name, sharePercentage: advisor.sharePercentage })));
		setEditingAdvisorsOf(enrollment.id);
	};

	const saveAdvisors = async (enrollment: EnrollmentHistoryDTO) => {
		setSavingAdvisors(true);
		setAdvisorsError(null);
		try {
			await http.enrollmentService.updateAdvisors(campusId, studentId, enrollment.id, advisorsDraft ?? []);
			setEditingAdvisorsOf(null);
			load();
		} catch (e) {
			setAdvisorsError(getErrorMessage(e));
		} finally {
			setSavingAdvisors(false);
		}
	};

	const attendanceText = (enrollment: EnrollmentHistoryDTO): string => {
		const attendance = enrollment.attendance;
		if (!attendance || attendance.total < 1) {
			return Constants.Common.noClassesYet;
		}
		return `${attendance.present}/${attendance.total} ${Constants.Common.classesRegistered} · ${attendance.rate}%`;
	};

	return (
		<div style={{ padding: "10px 5px", textAlign: "left" }}>
			<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
				<h6 style={{ margin: 0 }}>{Constants.Common.enrollments}</h6>
				{canEnroll ? (
					<button type="button" className="btn btn-primary rounded-pill" onClick={() => setShowEnroll(true)}>
						<FontAwesomeIcon icon={solid("user-plus")} />
						&nbsp;{Constants.Common.enroll}
					</button>
				) : ""}
			</div>
			{error ? <div className="alert alert-danger" style={{ fontSize: 13 }}>{error}</div> : ""}
			{history === null ? <div className="loader" /> : (history.length < 1 ? <div style={{ padding: 15 }}>-Sin registros-</div> : (
				history.map((enrollment) => (
					<div key={"h_" + enrollment.id} className="card" style={{ marginBottom: 10, borderLeft: `4px solid ${enrollment.isCurrentCampus ? "var(--main-color)" : "var(--wa-filo)"}` }}>
						<div className="card-body" style={{ padding: "10px 14px" }}>
							<div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
								<div style={{ flex: 1, minWidth: 0 }}>
									<b>{enrollment.courseName}</b> · {enrollment.levelName}
									{/*
										"Domingo 4:00 – 7:30 pm [Horario SCVQGN33QGP]": el horario en am/pm y una sola vez.
										Antes salía el nombre crudo en 24 h y además las horas de sesión, que dicen lo mismo;
										y la fecha de término, que nadie consulta aquí (Esteban, 2026-09-23).
									*/}
									<div style={{ fontSize: 13, color: "var(--wa-apagado)" }}>
										{horarioBonito(enrollment.scheduleName, { separador: " ", compacto: true })}
										{enrollment.scheduleCode ? ` [Horario ${enrollment.scheduleCode}]` : ""}
									</div>
									<div style={{ fontSize: 13, color: "var(--wa-apagado)" }}>
										{enrollment.periodName} · {Constants.Common.startsOn} {formatLocalDateTime(enrollment.startDate)}
										{enrollment.mentorName ? <> · {Constants.Common.mentor}: {enrollment.mentorName}</> : ""}
									</div>
									{(enrollment.buildingName || enrollment.classroomName) ? (
										<div style={{ fontSize: 13, color: "var(--wa-apagado)" }}>
											{[enrollment.buildingName, enrollment.classroomName].filter(Boolean).join(" · ")}
										</div>
									) : ""}
									<div style={{ fontSize: 13, marginTop: 2 }}>
										<span style={{ color: "var(--wa-apagado)" }}>{Constants.Common.enrollmentAttendance}:</span> {attendanceText(enrollment)}
									</div>
								</div>
								<div style={{ flexShrink: 0, textAlign: "right", display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
									<Badge bg={Utils.renderStatusEnrollment(enrollment.statusId as any)}>{enrollment.status}</Badge>
									{!enrollment.isCurrentCampus ? <Badge bg="light" text="dark">{Constants.Common.otherCampus}</Badge> : ""}
								</div>
							</div>
							<hr style={{ margin: "8px 0" }} />
							{editingAdvisorsOf === enrollment.id ? (
								<div style={{ marginBottom: 8 }}>
									<AdvisorsPicker campusId={campusId} value={advisorsDraft} onChange={setAdvisorsDraft} disabled={savingAdvisors} />
									{advisorsError ? <div className="text-danger" style={{ fontSize: 13 }}>{advisorsError}</div> : ""}
									<div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
										<button type="button" className="btn btn-outline-secondary btn-sm rounded-pill" disabled={savingAdvisors} onClick={() => setEditingAdvisorsOf(null)}>
											{Constants.Common.close}
										</button>
										<button type="button" className="btn btn-primary btn-sm rounded-pill" disabled={savingAdvisors} onClick={() => saveAdvisors(enrollment)}>
											{Constants.Common.saveChanges}
										</button>
									</div>
								</div>
							) : (
								<div>
									{enrollment.isCurrentCampus ? (
										<div style={{ display: "flex", justifyContent: "flex-end", gap: 14, marginBottom: 4 }}>
											{(canEnroll && enrollment.statusId === ENROLLMENT_STATUS.ACTIVE) ? (
												<button type="button" className="btn btn-link btn-sm" style={{ padding: 0, fontSize: 12 }} onClick={() => setUpdatingEnrollmentId(enrollment.id)}>
													<FontAwesomeIcon icon={solid("arrows-rotate")} />&nbsp;{Constants.Common.updateEnrollment}
												</button>
											) : ""}
											{/* Mismo criterio que el servidor: quien puede editar alumnos (Esteban, 2026-09-24). */}
											{canEnroll ? (
												<button type="button" className="btn btn-link btn-sm" style={{ padding: 0, fontSize: 12 }} onClick={() => startEditingAdvisors(enrollment)}>
													<FontAwesomeIcon icon={solid("pen")} />&nbsp;{Constants.Common.editAdvisors}
												</button>
											) : ""}
										</div>
									) : ""}
									<EnrollmentNotesTimeline
										notes={enrollment.notes}
										createdAt={enrollment.createdAt}
										createdBy={enrollment.createdBy}
										advisors={enrollment.advisors}
										statusId={enrollment.statusId}
										status={enrollment.status}
										updatedAt={enrollment.updatedAt}
										updatedBy={enrollment.updatedBy}
									/>
								</div>
							)}
						</div>
					</div>
				))
			))}
			<ModalComponent title={Constants.Common.updateEnrollment} showModal={updatingEnrollmentId !== null} onClose={closeUpdate}>
				{updatingEnrollmentId === null ? <></> : (
					<UpdateStudentEnrollmentComponent
						student={{ id: studentId, firstName: studentName.split(" ")[0] || "", lastName: studentName.split(" ").slice(1).join(" "), identityCode: studentIdentityCode || "" }}
						enrollmentId={updatingEnrollmentId}
						onSuccess={() => { closeUpdate(); load(); }}
					/>
				)}
			</ModalComponent>
			{showEnroll ? (
				<EnrollStudentModal
					studentId={studentId}
					studentName={studentName}
					campusId={campusId}
					onClose={() => setShowEnroll(false)}
					onEnrolled={() => { setShowEnroll(false); load(); if (onEnrolled) { onEnrolled(); } }}
				/>
			) : ""}
		</div>
	);
};

export default StudentHistoryTab;
